// AuthenticateAs.tsx - Enhanced Version
import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import {
  PublicClientApplication,
  AccountInfo,
  LogLevel,
  InteractionRequiredAuthError
} from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Paper,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
  IconButton
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import TokenIcon from "@mui/icons-material/Token";
import ApiIcon from "@mui/icons-material/Api";
import { Application } from "@microsoft/microsoft-graph-types";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// A small helper type for the dropdown items
interface ApiAudienceOption {
  label: string;         // e.g. "Microsoft Graph API" or "My Custom API #1"
  resourceAppId: string; // e.g. "00000003-0000-0000-c000-000000000000" or a custom GUID
  identifierUris?: string[]; // Application ID URIs
  exposedScopes?: ExposedScope[]; // Exposed scopes for this API
}

// A known constant for Microsoft Graph
const MS_GRAPH_APP_ID = "00000003-0000-0000-c000-000000000000";

/**
 * Interface for the parent's tested-user session.
 * Adjust the interface name/shape to match how you defined it in AppTester.
 */
interface TestUserSession {
  testedMsalInstance: PublicClientApplication | null;
  testerAccount: AccountInfo | null;
  rawIdToken: string | null;
  rawAccessToken?: string | null;
}

/**
 * Props for our AuthenticateAs component:
 * - The Application object.
 * - The parent's tested-user session and its setter.
 * - A callback to deliver decoded access tokens (optional).
 */
interface AuthenticateAsProps {
  app: Application;
  testUserSession: TestUserSession;
  setTestUserSession: React.Dispatch<React.SetStateAction<TestUserSession>>;
  onDecodedTokenTested?: (decoded: any) => void;
}

interface ExposedScope {
  id: string;
  value: string;
  description?: string;
  consentDisplayName?: string;
}

const AuthenticateAs: React.FC<AuthenticateAsProps> = ({
  app,
  testUserSession,
  setTestUserSession,
  onDecodedTokenTested
}) => {
  const { instance, accounts } = useMsal();
  const { testedMsalInstance, testerAccount, rawIdToken } = testUserSession;

  // The item the user picks in the dropdown
  const [selectedAudience, setSelectedAudience] = useState<string>("");

  // We store a list of valid audience options:
  const [audienceOptions, setAudienceOptions] = useState<ApiAudienceOption[]>([]);

  // For “loading” spinner while we fetch friendly names for custom APIs
  const [loading, setLoading] = useState<boolean>(false);

  const [useCustomScope, setUseCustomScope] = useState<boolean>(false);
  const [customScope, setCustomScope] = useState<string>("");

  const [rawAccessToken, setRawAccessToken] = useState<string>(testUserSession.rawAccessToken || "");

  const [showScopes, setShowScopes] = useState(false);

  /**
   * On mount, gather resourceAppIds from requiredResourceAccess
   * and fetch friendly names for them in Graph, if not the well-known Graph appId.
   */
  useEffect(() => {
    if (!app || !app.requiredResourceAccess) return;
  
    // Step 1: Always include Graph up front
    const newOptions: ApiAudienceOption[] = [
      {
        label: "Microsoft Graph API",
        resourceAppId: MS_GRAPH_APP_ID,
      },
    ];
  
    // Step 2: Find custom resource IDs
    const customRra = app.requiredResourceAccess.filter(
      (rra) => rra.resourceAppId !== MS_GRAPH_APP_ID
    );
    if (customRra.length === 0) {
      setAudienceOptions(newOptions);
      return;
    }
  
    // Step 3: For each custom resource, fetch a friendlier display name and scope information
    setLoading(true);
    getGraphToken()
      .then(async (token) => {
        const client = Client.init({ authProvider: (done) => done(null, token) });
  
        for (const r of customRra) {
          if (!r.resourceAppId) continue;
  
          try {
            // Get the service principal info
            const spResp = await client
              .api(`/servicePrincipals?$filter=appId eq '${r.resourceAppId}'`)
              .select("id,appId,displayName,appRoles,oauth2PermissionScopes,identifierUris")
              .get();
              
            if (spResp.value?.length) {
              const sp = spResp.value[0];
              
              // Now try to get the application to get the identifier URIs
              const appResp = await client
                .api(`/applications?$filter=appId eq '${r.resourceAppId}'`)
                .select("identifierUris")
                .get();
                
              const identifierUris = appResp.value?.[0]?.identifierUris || sp.identifierUris || [];
              
              // Extract exposed scopes from the oauth2PermissionScopes
              const exposedScopes = (sp.oauth2PermissionScopes || []).map((scope: any) => ({
                id: scope.id,
                value: scope.value,
                description: scope.description,
                consentDisplayName: scope.adminConsentDisplayName || scope.userConsentDisplayName
              }));
              
              newOptions.push({
                label: sp.displayName || r.resourceAppId,
                resourceAppId: r.resourceAppId,
                identifierUris,
                exposedScopes
              });
            } else {
              // Fallback if not found
              newOptions.push({
                label: `Custom API: ${r.resourceAppId}`,
                resourceAppId: r.resourceAppId,
              });
            }
          } catch (err) {
            console.error("Failed to fetch API details:", err);
            newOptions.push({
              label: `Custom API: ${r.resourceAppId}`,
              resourceAppId: r.resourceAppId,
            });
          }
        }
        
        setAudienceOptions(newOptions);
        
        // Set the default selected audience after options are loaded
        if (newOptions.length > 0 && !selectedAudience) {
          setSelectedAudience(newOptions[0].resourceAppId);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [app]);
  
  // Add this function to retrieve scope information for the selected API
  const getSelectedApiInfo = () => {
    if (!selectedAudience) return null;
    return audienceOptions.find(option => option.resourceAppId === selectedAudience);
  };

  // NEW: preferred resource URI for scope construction (identifierUris[0] or api://{appId})
  const getPreferredResourceUri = () => {
    const info = getSelectedApiInfo();
    if (!info) return "";
    return (info.identifierUris && info.identifierUris.length > 0)
      ? info.identifierUris[0]
      : `api://${info.resourceAppId}`;
  };

  /**
   * Acquire a Graph token for enumerating service principals, etc.
   */
  async function getGraphToken(): Promise<string> {
    const request = {
      scopes: ["Directory.Read.All"],
      account: accounts[0],
    };
    try {
      const resp = await instance.acquireTokenSilent(request);
      return resp.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const resp = await instance.acquireTokenPopup(request);
        return resp.accessToken;
      }
      throw err;
    }
  }

  /**
   * Event: user picks a new audience from the <Select>
   */
  const handleAudienceChange = (event: SelectChangeEvent<string>) => {
    setSelectedAudience(event.target.value);
  };

  /**
   * Start Authentication with the tested app, requesting an access token
   * for whichever audience the user selected.
   */
const startAuthentication = async () => {
  if (!app?.appId) {
    alert("No application selected for testing.");
    return;
  }
  if (window.opener) {
    alert("Authentication must be performed in the main browser window.");
    return;
  }

  // Get selected API information
  const selectedApiInfo = getSelectedApiInfo();
  if (!selectedApiInfo) {
    alert("Unable to determine API information. Please select an API audience first.");
    return;
  }
  
  try {
    setLoading(true);
    
    // Step 1: First determine if the target API is single or multi-tenant
    let targetApiTenantId = null;
    let isSingleTenantApi = false;
    
    // Skip this check for Microsoft Graph - it's always multi-tenant
    if (selectedApiInfo.resourceAppId !== MS_GRAPH_APP_ID) {
      const graphToken = await getGraphToken();
      const client = Client.init({ authProvider: (done) => done(null, graphToken) });
      
      try {
        // Get service principal details for the target API
        const spResp = await client
          .api(`/servicePrincipals?$filter=appId eq '${selectedApiInfo.resourceAppId}'`)
          .select("appOwnerOrganizationId,signInAudience")
          .get();
          
        if (spResp.value?.length) {
          const sp = spResp.value[0];
          // appOwnerOrganizationId is the tenant ID where the app is registered
          targetApiTenantId = sp.appOwnerOrganizationId;
          
          // Check if the API is single-tenant
          isSingleTenantApi = sp.signInAudience === "AzureADMyOrg";
          
          console.log("Target API details:", {
            appId: selectedApiInfo.resourceAppId,
            tenantId: targetApiTenantId,
            isSingleTenant: isSingleTenantApi
          });
        }
      } catch (err) {
        console.warn("Unable to determine if API is single-tenant, will try authentication anyway:", err);
      }
    }
    
    // Step 2: Determine the proper authority to use based on the app and API types
    
    // If we're targeting a single-tenant API, we MUST use its specific tenant
    // regardless of whether our app is multi-tenant or not
    let chosenTenant;
    
    if (isSingleTenantApi && targetApiTenantId) {
      // If API is single-tenant, we must use its tenant ID regardless
      chosenTenant = targetApiTenantId;
      console.log(`Using API's tenant ID (${targetApiTenantId}) for single-tenant API`);
    } else {
      // Otherwise use the tenant based on the tested app configuration
      const isSingleTenantApp = (app.signInAudience === "AzureADMyOrg");
      
      if (isSingleTenantApp) {
        // For single-tenant app, use its tenant
        chosenTenant = (app.publisherDomain || accounts[0]?.tenantId || "organizations");
        console.log(`Using app's tenant (${chosenTenant}) for single-tenant app`);
      } else {
        // For multi-tenant app accessing multi-tenant API, use common
        chosenTenant = "common";
        console.log("Using /common endpoint for multi-tenant app and API");
      }
    }

    const interactiveRedirectUri = window.location.origin;
    const silentRedirectUri = `${window.location.origin}/silent-end.html`;

    // Decide which scope to request
    // NEW: use an array; when using /.default, AAD will include all previously consented scopes
    let chosenScopes: string[] = [];

    if (useCustomScope && customScope) {
      // Accept one or multiple scopes (space/comma separated). Canonicalize if bare values are provided.
      const base = getPreferredResourceUri();
      chosenScopes = customScope
        .split(/[,\s]+/g)
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => (s.includes("://") ? s : `${base}/${s}`)); // ensure fully qualified
    } else if (selectedAudience === MS_GRAPH_APP_ID) {
      chosenScopes = ["https://graph.microsoft.com/User.Read"];
    } else {
      // Default behavior for custom APIs: request all pre-consented scopes
      const base = getPreferredResourceUri();
      chosenScopes = [`${base}/.default`];
    }

    console.log(`Requesting scopes: ${chosenScopes.join(" ")}`);

    // Build an MSAL instance for the tested app
    const testedMsal = new PublicClientApplication({
      auth: {
        clientId: app.appId,
        authority: `https://login.microsoftonline.com/${chosenTenant}`,
        redirectUri: interactiveRedirectUri,
      },
      cache: {
        // CRITICAL: Use localStorage to isolate test app from main app's sessionStorage
        // This prevents test app logout from clearing main app tokens
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            if (message.includes("block_nested_popups")) return;
            console.log(`[TestedAppMSAL][${LogLevel[level]}]`, message);
          },
        },
      },
    });

    await testedMsal.initialize();

    const loginResponse = await testedMsal.loginPopup({
      scopes: chosenScopes,
      prompt: "select_account",
    });

    // Acquire an access token
    const tokenResponse = await testedMsal.acquireTokenSilent({
      scopes: chosenScopes,
      account: loginResponse.account,
      redirectUri: silentRedirectUri,
    });
    setRawAccessToken(tokenResponse.accessToken);
    
    // Save the tested-app session in parent state
    setTestUserSession({
      testedMsalInstance: testedMsal,
      testerAccount: loginResponse.account || null,
      rawIdToken: loginResponse.idToken || null,
      rawAccessToken: tokenResponse.accessToken 
    });  

    console.log("Access token set:", tokenResponse.accessToken ? "✓ Token received" : "✗ No token");
    console.log("Raw access token state:", rawAccessToken ? "✓ State has token" : "✗ State empty");

    // Optionally decode the access token
    const decoded = decodeToken(tokenResponse.accessToken);
    if (onDecodedTokenTested) {
      onDecodedTokenTested(decoded);
    }
  } catch (error: unknown) {
    console.error("Authentication error:", error);
    
    // Add specific handling for single-tenant specific errors
    if (error instanceof Error) {
      if (error.message.includes("AADSTS50194")) {
        alert("Authentication failed: You're trying to access a single-tenant API using a multi-tenant app. This tool will now attempt to detect the correct tenant and use it for authentication.");
        
        // Here you could implement an automatic retry with the correct tenant
      } else if (error.message.includes("AADSTS50020")) {
        alert("Authentication failed: The user account doesn't exist in the tenant directory. This is common when trying to sign in with a user from another tenant for a single-tenant app.");
      } else if (error.message.includes("AADSTS90002")) {
        alert("Authentication failed: The tenant specified in the request doesn't exist or couldn't be found.");
      } else {
        alert("Authentication failed: " + error.message);
      }
    } else {
      alert("Authentication failed. See console for details.");
    }
  } finally {
    setLoading(false);
  }
};
  /**
   * Sign out from the tested app
   */
  const signOutTestedApp = async () => {
    if (!testedMsalInstance || !testerAccount) {
      alert("No tested app session found.");
      return;
    }
    if (window.opener) {
      alert("Sign-out must occur in the main window, not a popup.");
      return;
    }

    try {
      await testedMsalInstance.logoutPopup({
        account: testerAccount,
        postLogoutRedirectUri: `${window.location.origin}/silent-end.html`
      });
    } catch (error) {
      console.error("Error during sign-out:", error);
    } finally {
      // Always clean up session regardless of whether logoutPopup succeeded or threw
      // (COOP on Entra ID's logout page can cause logoutPopup to throw even on success)
      setTestUserSession({
        testedMsalInstance: null,
        testerAccount: null,
        rawIdToken: null,
        rawAccessToken: null
      });
      setCustomScope("");
      setRawAccessToken("");
      if (onDecodedTokenTested) {
        onDecodedTokenTested(null);
      }
    }
  };

  /**
   * A small helper to decode a JWT
   */
  function decodeToken(token: string) {
    if (!token) return null;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading custom API names...
        </Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent
        sx={{
          background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
          borderBottom: '1px solid #c5cae9',
          mb: 0,
          pb: 2,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <TokenIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontSize: "1.2rem",
              fontWeight: 600,
              color: "#283593",
              letterSpacing: "0.01em",
              flexGrow: 1
            }}
          >
            Authenticate as Test User
          </Typography>
          <Chip
            size="small"
            label="Testing"
            sx={{
              backgroundColor: '#c5cae9',
              color: '#283593',
              fontWeight: 500
            }}
          />
        </Box>
      </CardContent>      
      
      <CardContent>
        {testerAccount ? (
          // AUTHENTICATED STATE
          <>
            {/* Current Session Accordion */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "primary.main",
                    letterSpacing: "0.01em",
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <PersonIcon sx={{ mr: 1 }} />
                  Current Testing Session
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ backgroundColor: "#fafafa", borderRadius: 1, p: 3 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3,
                  border: '1px solid #e8eaf6',
                  borderLeft: '4px solid #3f51b5', // Indigo accent
                  borderRadius: '12px',
                  backgroundColor: '#fafbff' // Match theme
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}
                    >
                      <PersonIcon sx={{ color: 'white', fontSize: '1rem' }} />
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontWeight: 600,
                        color: "#283593"
                      }}
                    >
                      Authenticated as: <strong>{testerAccount.name || testerAccount.username}</strong>
                    </Typography>
                  </Box>
                    
                    <Box sx={{ textAlign: 'center' }}>
                      <Chip 
                        label={`App: ${app.displayName}`} 
                        color="primary" 
                        sx={{ fontWeight: 500, mb: 1 }}
                      />
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click <VisibilityIcon fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.5 }} /> 
                        in your "Decoded Token" panel to view the access token details.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="error" 
                    startIcon={<LogoutIcon />}
                    onClick={signOutTestedApp}
                    fullWidth
                    sx={{ 
                      py: 1.2,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 2px 4px rgba(211, 47, 47, 0.2)',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(211, 47, 47, 0.3)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Sign Out from Tested App
                  </Button>
                  
                  {rawIdToken && (
                    <Button 
                      variant="outlined"
                      color="secondary"
                      startIcon={<ContentCopyIcon />}
                      fullWidth
                      sx={{ 
                        py: 1.2,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(123, 31, 162, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(123, 31, 162, 0.3)',
                          transform: 'translateY(-1px)',
                          borderColor: '#7b1fa2',
                          backgroundColor: 'rgba(123, 31, 162, 0.04)'
                        }
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(rawIdToken).then(
                          () => alert("Raw ID token copied!"),
                          (err) => alert("Clipboard copy failed: " + err)
                        );
                      }}
                    >
                      Copy Raw ID Token
                    </Button>
                  )}

                  {/* ADD: Raw Access Token Copy Button */}
                  {rawAccessToken && (
                    <Button 
                      variant="outlined"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      fullWidth
                      sx={{ 
                        py: 1.2,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                          transform: 'translateY(-1px)',
                          borderColor: '#1976d2',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(rawAccessToken).then(
                          () => alert("Raw access token copied!"),
                          (err) => alert("Clipboard copy failed: " + err)
                        );
                      }}
                    >
                      Copy Raw Access Token
                    </Button>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
            
            {/* Token Information Accordion */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "primary.main",
                    letterSpacing: "0.01em",
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <TokenIcon sx={{ mr: 1 }} />
                  Token Information
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ backgroundColor: "#f7f7f7", borderRadius: 1, p: 3 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  This section displays technical information about your authentication session.
                </Alert>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Authentication Details:
                  </Typography>
                  
                  <Stack spacing={1} sx={{ ml: 2 }}>
                    <Typography variant="body2">
                      <strong>Testing Account:</strong> {testerAccount.username}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Account ID:</strong> {testerAccount.homeAccountId.split('.')[0]}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Application:</strong> {app.displayName} ({app.appId})
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tenant:</strong> {testerAccount.tenantId || "Unknown"}
                    </Typography>
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          // UNAUTHENTICATED STATE
          <>
            {/* Authentication Steps Accordion */}
            <Accordion defaultExpanded>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  backgroundColor: 'rgba(63, 81, 181, 0.04)', // Light indigo background
                  '&.Mui-expanded': {
                    minHeight: 56,
                    borderBottom: '1px solid #c5cae9' // Indigo border
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#283593", // Indigo color
                    letterSpacing: "0.01em",
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <LockOpenIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  Authentication Steps
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ backgroundColor: "#fafafa", borderRadius: 1, p: 3 }}>
                {/* A) Audience Selection */}
                <Box 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    backgroundColor: "#fafbff", // Match main theme
                    borderRadius: '12px', // Consistent with other components
                    border: '1px solid #e8eaf6' // Indigo border
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        backgroundColor: '#3f51b5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}
                    >
                      <ApiIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "#283593",
                        letterSpacing: "0.01em"
                      }}
                    >
                      1. Select Token Audience
                    </Typography>
                  </Box>
                  
                  <FormControl fullWidth>
                    <InputLabel>API Audience</InputLabel>
                    <Select
                      value={selectedAudience}
                      label="API Audience"
                      onChange={handleAudienceChange}
                      sx={{ 
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c5cae9',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        }
                      }}
                    >
                      {audienceOptions.map((opt) => (
                        <MenuItem key={opt.resourceAppId} value={opt.resourceAppId}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedAudience && selectedAudience !== MS_GRAPH_APP_ID && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      {/* <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Available Scope Information
                      </Typography> */}

                      {(() => {
                        const apiInfo = getSelectedApiInfo();

                        if (!apiInfo) return (
                          <Typography variant="body2" color="text.secondary">
                            No scope information available for the selected API.
                          </Typography>
                        );

                        return (
                          <>
                            {/* Keep: Clickable Application ID URIs → auto-fill custom scope with /.default */}
                            {apiInfo && Array.isArray(apiInfo.identifierUris) && apiInfo.identifierUris.length > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Application ID URI:
                                </Typography>
                                <Chip
                                  label={apiInfo.identifierUris[0]}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  onClick={() => {
                                    setUseCustomScope(true);
                                    setCustomScope(`${apiInfo.identifierUris![0]}/.default`);
                                  }}
                                  sx={{ m: 0.5, cursor: 'pointer' }}
                                />
                                {/* <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                  <HelpOutlineIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                  <Typography variant="caption" color="text.secondary">
                                  Click on an Application ID URI to use it with the /.default scope for the selected API.
                                  </Typography>
                                </Box> */}

                                <Divider sx={{ my: 2, borderColor: '#707098ff' }} />
                                
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    mb: 1,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: showScopes ? '#e3f2fd' : '#f5f5f5',
                                    fontWeight: 600,
                                    color: showScopes ? 'primary.main' : 'text.secondary',
                                    transition: 'background 0.2s',
                                    boxShadow: showScopes ? '0 2px 8px rgba(33,150,243,0.08)' : 'none'
                                  }}
                                  onClick={() => setShowScopes((prev) => !prev)}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 'medium', mr: 1 }}>
                                    {showScopes ? "Hide Exposed Scopes" : "Show Exposed Scopes"}
                                  </Typography>
                                  {showScopes ? <ExpandMoreIcon color="primary" /> : <ChevronRightIcon color="action" />}
                                </Box>
                                {showScopes && Array.isArray(apiInfo.exposedScopes) && apiInfo.exposedScopes.length > 0 && (
                                  <Box sx={{ maxHeight: '150px', overflowY: 'auto', mt: 1 }}>
                                    {apiInfo.exposedScopes.map((scope) => {
                                      const fullScope = `${getPreferredResourceUri()}/${scope.value}`;
                                      return (
                                        <Box
                                          key={scope.id}
                                          sx={{
                                            mb: 1,
                                            p: 1,
                                            backgroundColor: '#ffffff',
                                            borderRadius: 1,
                                            border: '1px solid #e0e0e0'
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', wordBreak: 'break-all' }}>
                                            {fullScope}
                                          </Typography>
                                          {scope.description && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              {scope.description}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            )}

                            {(!apiInfo.identifierUris || apiInfo.identifierUris.length === 0) &&
                            (!apiInfo.exposedScopes || apiInfo.exposedScopes.length === 0) && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                No scope information could be retrieved automatically.
                              </Alert>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}               

                    {/* ADD CUSTOM SCOPE OPTION HERE */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useCustomScope}
                          onChange={(e) => setUseCustomScope(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Specify custom scope format"
                    />
                  </Box>

                  {useCustomScope && (
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        label="Custom Scope"
                        variant="outlined"
                        margin="normal"
                        value={customScope}
                        onChange={(e) => setCustomScope(e.target.value)}
                        placeholder="e.g., api://app-id/.default or https://contoso.com/api/.default"
                        helperText="Enter the complete scope URI including the .default suffix if needed."
                        InputProps={{
                          endAdornment: customScope ? (
                            <IconButton 
                              size="small" 
                              onClick={() => setCustomScope("")}
                              sx={{ position: 'absolute', right: 10, top: 16 }}
                              aria-label="Clear custom scope"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          ) : null
                        }}
                      />
                    </Box>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.85rem' }}>
                    This determines which API your access token will be valid for.
                  </Typography>
                </Box>
  
                <Divider sx={{ my: 3 }} />
  
                {/* B) Start Authentication */}
                <Box 
                  sx={{ 
                    p: 3, 
                    backgroundColor: "#fafbff", 
                    borderRadius: '12px',
                    border: '1px solid #e8eaf6',
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        backgroundColor: '#3f51b5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}
                    >
                      <LockOpenIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "#283593",
                        letterSpacing: "0.01em"
                      }}
                    >
                      2. Authenticate with {app.displayName}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ 
                      mt: 1,
                      borderRadius: '8px', // Consistent with other components
                      padding: '0.8rem 2rem',
                      background: 'linear-gradient(135deg, #3f51b5 0%, #7986cb 100%)',
                      boxShadow: '0 2px 12px rgba(63, 81, 181, 0.3)',
                      textTransform: 'none',
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(63, 81, 181, 0.4)',
                        background: 'linear-gradient(135deg, #303f9f 0%, #5c6bc0 100%)',
                      }
                    }}
                    onClick={startAuthentication}
                    startIcon={<LockOpenIcon />}
                  >
                    Start Authentication
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
            
            {/* About OAuth Authentication */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography 
                  variant="subtitle1"
                  sx={{ 
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.1rem", color: 'info.main' }} />
                  About Authentication Testing
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails>
                <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    This tool allows you to test the authentication flow for your application and inspect the resulting tokens.
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#ffffff', 
                    border: '1px solid #e0e0ff', 
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                      What Happens During Authentication:
                    </Typography>
                    <Typography variant="body2">
                      • <strong>User Sign-In</strong>: You will be prompted to sign in with a test user account<br />
                      • <strong>Consent</strong>: You may need to consent to permissions requested by the app<br />
                      • <strong>Token Issuance</strong>: After successful authentication, ID and access tokens are issued<br />
                      • <strong>Token Inspection</strong>: You can view the decoded tokens to verify claims and permissions
                    </Typography>
                  </Box>
                  
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                      Best Practices for Testing:
                    </Typography>
                    <Typography variant="body2">
                      • Use different test accounts with varying permissions to verify access control<br />
                      • Test with both consumer and work accounts if your app supports both<br />
                      • Verify that token claims match your expectations (roles, groups, etc.)<br />
                      • Check that token scopes match what your application requested
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AuthenticateAs;